import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';

const COLORS = ['#1f5eff', '#c98b12'];

function AgentCompare({ filters, loading, setLoading }) {
  const [agents, setAgents] = useState([]);
  const [agent1, setAgent1] = useState('');
  const [agent2, setAgent2] = useState('');
  const [comparison, setComparison] = useState(null);
  const [stats, setStats] = useState([]);
  const [compLoading, setCompLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });
    return params;
  }, [filters]);

  useEffect(() => {
    const params = buildParams();
    axios.get(`/api/agent-view/agents?${params}`)
      .then(res => setAgents(res.data?.data || []))
      .catch(() => {});
  }, [filters, buildParams]);

  useEffect(() => {
    setStatsLoading(true);
    const params = buildParams();
    axios.get(`/api/agent-view/stats?${params}`)
      .then(res => setStats(res.data?.data || []))
      .catch(() => {})
      .finally(() => { setStatsLoading(false); setLoading(false); });
  }, [filters, buildParams, setLoading]);

  useEffect(() => {
    if (!agent1 || !agent2 || agent1 === agent2) {
      setComparison(null);
      return;
    }
    setCompLoading(true);
    const params = buildParams();
    params.append('agent1_id', agent1);
    params.append('agent2_id', agent2);
    axios.get(`/api/agent-view/comparison?${params}`)
      .then(res => setComparison(res.data))
      .catch(() => {})
      .finally(() => setCompLoading(false));
  }, [agent1, agent2, filters, buildParams]);

  const selectStyle = {
    border: '1.5px solid var(--card-border)', borderRadius: '12px',
    padding: '10px 14px', fontSize: '14px', background: 'var(--card-bg)',
    color: 'var(--text-dark)', outline: 'none', width: '100%', boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
  const labelStyle = {
    fontSize: '10px', fontWeight: 900, textTransform: 'uppercase',
    letterSpacing: '0.18em', color: 'var(--text-lighter)',
  };

  return (
    <div>
      <FilterBar />

      {/* Agent Comparison */}
      <Panel title="Agent Entity Comparison" subtitle="Compare entity mention frequency between two agents (counted once per conversation)">
        <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={labelStyle}>Agent A</label>
            <select style={selectStyle} value={agent1} onChange={e => setAgent1(e.target.value)}>
              <option value="">Select agent...</option>
              {agents.map(a => (
                <option key={a.agent_id} value={a.agent_id}>{a.agent_name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={labelStyle}>Agent B</label>
            <select style={selectStyle} value={agent2} onChange={e => setAgent2(e.target.value)}>
              <option value="">Select agent...</option>
              {agents.map(a => (
                <option key={a.agent_id} value={a.agent_id}>{a.agent_name}</option>
              ))}
            </select>
          </div>
        </div>

        {agent1 && agent2 && agent1 === agent2 && (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-lighter)', fontSize: '14px' }}>
            Please select two different agents to compare.
          </div>
        )}

        {compLoading && (
          <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-lighter)' }}>Loading...</div>
        )}

        {!compLoading && comparison && comparison.data && comparison.data.length > 0 && (
          <Plot
            data={[
              {
                x: comparison.data.map(d => d.entity_name),
                y: comparison.data.map(d => Number(d.agent1_count)),
                name: comparison.agent1_name,
                type: 'bar',
                marker: { color: COLORS[0], borderRadius: 4 },
              },
              {
                x: comparison.data.map(d => d.entity_name),
                y: comparison.data.map(d => Number(d.agent2_count)),
                name: comparison.agent2_name,
                type: 'bar',
                marker: { color: COLORS[1], borderRadius: 4 },
              },
            ]}
            layout={{
              barmode: 'group',
              xaxis: { title: 'Entity', tickangle: -45, automargin: true },
              yaxis: { title: 'Mention Count' },
              margin: { t: 30, r: 20, b: 120, l: 60 },
              legend: { orientation: 'h', y: 1.12 },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { family: 'Inter, sans-serif', color: 'var(--text-dark)' },
            }}
            config={{ displayModeBar: false, responsive: true }}
            useResizeHandler
            style={{ width: '100%', height: '420px' }}
          />
        )}

        {!compLoading && comparison && comparison.data && comparison.data.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-lighter)', fontSize: '14px' }}>
            No entity mentions found for the selected agents in this time range.
          </div>
        )}

        {!compLoading && !comparison && agent1 && agent2 && agent1 !== agent2 && (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-lighter)', fontSize: '14px' }}>
            Select two agents above to compare their entity mentions.
          </div>
        )}
      </Panel>

      {/* Agent Stats Table */}
      <Panel title="Agent Statistics" subtitle="Conversation and entity metrics per agent" loading={statsLoading}>
        {stats.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--card-border)' }}>
                  <th style={thStyle}>Agent</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Conversations</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Entities / Conv</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Avg Length (chars)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Avg Words</th>
                </tr>
              </thead>
              <tbody>
                {stats.map(row => (
                  <tr key={row.agent_id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                    <td style={tdStyle}>{row.agent_name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{row.conversation_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{row.entities_per_conversation ?? '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avg_conversation_length != null ? Math.round(row.avg_conversation_length).toLocaleString() : '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{row.avg_word_count != null ? Math.round(row.avg_word_count).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-lighter)', fontSize: '14px' }}>
            No agent data available for the selected filters.
          </div>
        )}
      </Panel>
    </div>
  );
}

const thStyle = {
  padding: '12px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 900,
  textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--text-lighter)',
};
const tdStyle = {
  padding: '12px 14px', color: 'var(--text-dark)',
};

export default AgentCompare;
